/**
 * Pipeline Stage Definitions — Single source of truth for stage numbers.
 *
 * AUDIT v1.4 / Sprint 4 (C21, V1b) — Consolidates stage-number constants that
 * were previously hardcoded in:
 *   - stages/contentValidators/shared.ts  (was: STAGE = 3)
 *   - stages/typeValidators/shared.ts     (was: STAGE = 1)
 *   - several individual stage files       (inline literals)
 *
 * Any new stage-runner or validator MUST import its stage number from here so
 * that future renumbering happens in exactly one place.
 *
 * NOTE on stage 1/1b and 3/3b: type validation (1b) shares stage 1 with schema
 * validation, and pedagogy validation (3b) shares stage 3 with content
 * validation. This is by design — see PEDAGOGICAL_CONTRACT_SPEC §8b.6.
 */

import type { PipelineStage } from './issueContract'

/**
 * Canonical stage numbers, named by their semantic role.
 *
 * - SCHEMA   (1)   Stage 1   — structural/Zod validation
 * - TYPE     (1)   Stage 1b  — question-type validators (shares stage 1)
 * - GRAPH    (2)   Stage 2   — LDG/prerequisite validation
 * - CONTENT  (3)   Stage 3   — content quality validation
 * - PEDAGOGY (3)   Stage 3b  — pedagogical contract (shares stage 3)
 * - VISUAL   (4)   Stage 4   — visual aid validation
 * - SUPPORT  (5)   Stage 5   — support/hint validation
 * - SEED     (5)   Stage 5b  — seed consolidation warnings (shares stage 5)
 * - DIVERSITY (5)  Stage 5c  — batch-only diversity checks (shares stage 5)
 * - SCORING  (6)   Stage 6   — score computation (folded into gate)
 * - GATE     (7)   Stage 7   — final verdict
 */
export const STAGES = {
  SCHEMA: 1 as PipelineStage,
  TYPE: 1 as PipelineStage,
  GRAPH: 2 as PipelineStage,
  CONTENT: 3 as PipelineStage,
  PEDAGOGY: 3 as PipelineStage,
  VISUAL: 4 as PipelineStage,
  SUPPORT: 5 as PipelineStage,
  SEED: 5 as PipelineStage,
  DIVERSITY: 5 as PipelineStage,
  SCORING: 6 as PipelineStage,
  GATE: 7 as PipelineStage,
} as const

/** Human-readable stage role keys (TS-safe union). */
export type StageRole = keyof typeof STAGES

// NOTE: stab-ronde 3 (mei 2026) — `getStageNumber(role)` verwijderd: nul
// callers. Code die het stage-nummer voor een rol nodig heeft gebruikt
// direct `STAGES[role]` (idem performance, één indirection minder).
